<script setup>
import { onMounted, ref } from 'vue';
defineProps({ username: String, userId: String, avatar: String, isOwner: Boolean, switchedByAdmin: Boolean });
const emit = defineEmits(['switch-user', 'switch-back', 'logout', 'close']);
const menu = ref(null);
onMounted(() => menu.value?.querySelector('button')?.focus({ preventScroll: true }));
const handleKey = (event) => {
    if (event.key === 'Escape' || event.key === 'Tab') {
        if (event.key === 'Escape') event.preventDefault();
        emit('close');
        return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const items = [...menu.value.querySelectorAll('button')];
    const current = items.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
        : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus({ preventScroll: true });
};
</script>

<template>
    <div ref="menu" class="user-menu account-dropdown" role="menu" aria-label="Account" @keydown="handleKey">
        <div class="account-dropdown__identity" role="presentation">
            <img v-if="avatar" :src="avatar" alt="" />
            <i v-else class="pi pi-user" aria-hidden="true"></i>
            <div><span class="account-dropdown__name">{{ username }}</span><span class="account-dropdown__id">{{ userId }}</span></div>
        </div>
        <div class="account-dropdown__separator" role="separator"></div>
        <button v-if="isOwner" type="button" role="menuitem" class="account-dropdown__item" @click="emit('switch-user')">
            <i class="pi pi-user" aria-hidden="true"></i><span>Switch user</span>
        </button>
        <button v-if="switchedByAdmin" type="button" role="menuitem" class="account-dropdown__item" @click="emit('switch-back')">
            <i class="pi pi-arrow-left" aria-hidden="true"></i><span>Switch back</span>
        </button>
        <button type="button" role="menuitem" class="account-dropdown__item" @click="emit('logout', $event)">
            <i class="pi pi-sign-out" aria-hidden="true"></i><span>Log out</span>
        </button>
    </div>
</template>

<style scoped lang="scss">
.account-dropdown {
    position: fixed;
    left: 12px;
    bottom: 76px;
    width: 280px;
    padding: 8px;
    border: 1px solid var(--surface-border);
    border-radius: 22px;
    background: var(--surface-card);
    color: var(--text-color);
    box-shadow: 0 12px 32px rgb(0 0 0 / 12%), 0 2px 5px rgb(0 0 0 / 5%);
    z-index: 1100;
}
.account-dropdown__identity { display: flex; align-items: center; gap: 12px; padding: 12px 14px; }
.account-dropdown__identity > img { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
.account-dropdown__identity > i { width: 32px; text-align: center; font-size: 22px; }
.account-dropdown__identity > div { display: grid; gap: 4px; min-width: 0; }
.account-dropdown__name { font-size: 16px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.account-dropdown__id { font-size: 13px; color: var(--text-color-secondary); opacity: 0.65; }
.account-dropdown__separator { height: 1px; background: var(--surface-border); margin: 6px 14px; }
.account-dropdown__item {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 0;
    border-radius: 14px;
    background: transparent;
    color: inherit;
    text-align: left;
    font: inherit;
    font-size: 15px;
    cursor: pointer;
}
.account-dropdown__item > i { width: 20px; font-size: 19px; color: var(--text-color-secondary); }
.account-dropdown__item:hover, .account-dropdown__item:focus-visible { background: var(--surface-hover); outline: none; }
</style>
